import { Mark, mergeAttributes, type Editor } from "@tiptap/react"
import type { Mark as ProseMirrorMark } from "@tiptap/pm/model"
import type { Transaction } from "@tiptap/pm/state"
import { VocabularyEntryStateEnum } from "@/__generated__/graphql"
import type { CaptureRow } from "@/lib/live/captures-log"

// The marker on the highlighted text (#825 phase 2): a TipTap mark carrying the
// capture's entry id and storage state. Applied to the selection on capture, it
// replicates to the peer through the Collaboration binding exactly like bold;
// the state attribute drives the underline (index.css `.capture-mark[data-
// capture-state]`) and the hover title shows the translated chip label.
// syncCaptureMarks keeps every mark in step with the strip's rows.
export const CAPTURE_MARK = "capture"

export interface CaptureMarkAttrs {
  entryId: string
  state: VocabularyEntryStateEnum
}

export interface CaptureMarkOptions {
  // Translated hover label for a state. Rendered per viewer (never stored in
  // the document), so two peers in different UI locales each see their own.
  labelFor: (state: VocabularyEntryStateEnum) => string
}

export const CaptureMark = Mark.create<CaptureMarkOptions>({
  name: CAPTURE_MARK,
  // Typing right after a captured word must not extend the marker.
  inclusive: false,
  // Coexists with every other mark, including another capture on the same text.
  excludes: "",
  keepOnSplit: false,

  addOptions() {
    return { labelFor: (state) => state }
  },

  addAttributes() {
    return {
      entryId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-capture-entry"),
        renderHTML: (attributes) => ({ "data-capture-entry": attributes.entryId }),
      },
      state: {
        default: VocabularyEntryStateEnum.Pending,
        parseHTML: (element) => element.getAttribute("data-capture-state"),
        renderHTML: (attributes) => ({ "data-capture-state": attributes.state }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "span[data-capture-entry]" }]
  },

  renderHTML({ mark, HTMLAttributes }) {
    // Key order is DOM attribute order: class, the two data attributes, title.
    return [
      "span",
      mergeAttributes({ class: "capture-mark" }, HTMLAttributes, {
        title: this.options.labelFor(mark.attrs.state as VocabularyEntryStateEnum),
      }),
      0,
    ]
  },
})

// Marks a range as a capture. Outside the document history: an undo of the
// notes must never strip a marker the server still knows about.
//
// TOTAL BY CONTRACT — it can never throw. The capture it decorates has already
// succeeded on the server by the time we get here (it is in the strip and a
// card is being generated), so a marking problem must never travel up to the
// caller and be reported to the student as a failed capture. Every way the
// document can have moved under us is absorbed: a destroyed editor (destroy()
// nulls the schema), a range the document has shrunk past, a null range (the
// anchor no longer resolves to the captured text), a dispatch that blows up.
export function applyCaptureMark(editor: Editor, range: { from: number; to: number } | null, attrs: CaptureMarkAttrs): void {
  if (!range) return
  if (editor.isDestroyed) return
  const type = editor.schema.marks[CAPTURE_MARK]
  if (!type) return
  const size = editor.state.doc.content.size
  const from = Math.min(Math.max(range.from, 0), size)
  const to = Math.min(Math.max(range.to, 0), size)
  if (from >= to) return
  try {
    const tr = editor.state.tr
      .addMark(from, to, type.create({ entryId: attrs.entryId, state: attrs.state }))
      .setMeta("addToHistory", false)
    editor.view.dispatch(tr)
  } catch {
    // The document moved under us between the clamp and the dispatch. The
    // capture itself stands; only its underline is missing.
  }
}

// The range the marker will land on, kept current for as long as the capture
// mutation is in flight. The raw {from, to} read at click time is worthless
// ~200-800ms later: a peer's edit in that window shifts the whole document, so
// the underline would land on the WRONG WORDS (permanently — syncCaptureMarks
// reconciles mark STATE, never position) or past the end of it.
//
// The anchor is TipTap's MappablePosition, updated from every transaction the
// editor sees. This is NOT the same as `transaction.mapping.map(pos)`:
// y-tiptap applies a remote update as one whole-document `tr.replace`, which a
// naive mapping does not survive, but the Collaboration extension's
// CollaborationMappablePosition carries a Y.js relative position that does
// (@tiptap/extension-collaboration overrides editor.utils.* for exactly this).
// Always call stop() — a live listener holds the editor.
//
// current() returns null when the range it resolves to no longer holds the text
// that was captured: y-tiptap turns a relative position it can no longer
// resolve into 0 (`relativePositionToAbsolutePosition(...) || 0`), and one end
// failing yields {0, to} — an underline from the START OF THE DOCUMENT to the
// word, permanent because syncCaptureMarks reconciles state and never position.
// No underline is strictly better than that one.
export interface CaptureRangeAnchor {
  current(): { from: number; to: number } | null
  stop(): void
}

// The document text under a range, or null if the range cannot be read at all
// (out of bounds, a destroyed editor, an editor stub in a test).
function textAt(editor: Editor, from: number, to: number): string | null {
  try {
    if (from >= to) return null
    return editor.state.doc.textBetween(from, to)
  } catch {
    return null
  }
}

type Anchor = ReturnType<Editor["utils"]["createMappablePosition"]>

// Defensive: createMappablePosition only reads the ySync plugin state, so it
// cannot throw today. If it ever did, the throw would escape past WordPopover's
// try/finally and leave the Create button dead for the rest of the lesson.
function createAnchors(utils: Editor["utils"], range: { from: number; to: number }): { from: Anchor; to: Anchor } | null {
  try {
    return { from: utils.createMappablePosition(range.from), to: utils.createMappablePosition(range.to) }
  } catch {
    return null
  }
}

export function trackCaptureRange(editor: Editor, range: { from: number; to: number }): CaptureRangeAnchor {
  const utils = editor.utils
  // The text the student captured, read once at click time: what the resolved
  // range has to still hold for the marker to be the right words.
  const captured = textAt(editor, range.from, range.to)
  const verify = (next: { from: number; to: number }) => {
    if (captured === null) return next // nothing to compare against
    return textAt(editor, next.from, next.to) === captured ? next : null
  }
  // No position helpers (an editor from another TipTap major), or no anchor:
  // the raw range is all we have, and it is what the old code used anyway.
  if (!utils?.createMappablePosition || !utils.getUpdatedPosition) {
    return { current: () => verify({ ...range }), stop: () => {} }
  }
  const anchors = createAnchors(utils, range)
  if (!anchors) {
    return { current: () => verify({ ...range }), stop: () => {} }
  }
  let { from, to } = anchors
  const onTransaction = ({ transaction }: { transaction: Transaction }) => {
    // Inside the editor's own event emitter: a throw here would break the
    // dispatch that is running. Keep the last known-good anchors instead.
    try {
      from = utils.getUpdatedPosition(from, transaction).position
      to = utils.getUpdatedPosition(to, transaction).position
    } catch {
      /* keep the previous anchors */
    }
  }
  editor.on("transaction", onTransaction)
  return {
    current: () => verify({ from: from.position, to: to.position }),
    stop: () => {
      editor.off("transaction", onTransaction)
    },
  }
}

// Walks the document once and reconciles every capture mark with the strip's
// rows: a mark whose entry is missing or cancelled is removed, one whose state
// differs from its row is re-applied with the row's state. One transaction, or
// none when nothing changed (so a re-render never dispatches).
export function syncCaptureMarks(editor: Editor, rows: CaptureRow[]): void {
  if (editor.isDestroyed) return
  const type = editor.schema.marks[CAPTURE_MARK]
  if (!type) return
  const byEntry = new Map(rows.map((row) => [row.entryId, row]))
  const changes: Array<{ from: number; to: number; mark: ProseMirrorMark; row: CaptureRow | undefined }> = []

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return
    for (const mark of node.marks) {
      if (mark.type !== type) continue
      const row = byEntry.get(mark.attrs.entryId as string)
      const stale = !row || row.state === VocabularyEntryStateEnum.Cancelled || row.state !== mark.attrs.state
      if (stale) changes.push({ from: pos, to: pos + node.nodeSize, mark, row })
    }
  })
  if (changes.length === 0) return

  let tr = editor.state.tr
  for (const change of changes) {
    tr = tr.removeMark(change.from, change.to, change.mark)
    if (change.row && change.row.state !== VocabularyEntryStateEnum.Cancelled) {
      tr = tr.addMark(change.from, change.to, type.create({ entryId: change.row.entryId, state: change.row.state }))
    }
  }
  editor.view.dispatch(tr.setMeta("addToHistory", false))
}
